import { HttpInterceptorFn } from '@angular/common/http';
import { localStorageKeys, SCOPED_AUTH_SENTINEL_HEADER } from '../constants/common.const';

/**
 * INC-007a — attaches the EVENT_MARKETOLOG bearer, and only on the marketolog shell.
 *
 * Mirrors `owner-auth.interceptor.ts` with three substitutions: the storage key, the path guard
 * (`/marketolog`) and the URL guard. Registered FIRST in the chain, so on `/marketolog/**` it
 * wins over both the owner and the cabinet interceptors while an organizer stays signed in in
 * the same browser profile.
 *
 * The `X-Owner-Context: '1'` sentinel is reused verbatim — `authInterceptor` already strips it
 * and short-circuits on it (`auth.interceptor.ts:13-15`), so a second sentinel would be dead
 * weight.
 *
 * Once BOTH guards pass the request belongs to the marketolog shell, so the sentinel is set
 * unconditionally — **including when there is no usable marketolog session**. A bare `next(req)`
 * there was a cross-authentication bug: `ownerAuthInterceptor` skips (the path is not `/owner`)
 * and `authInterceptor` then attaches the cabinet ORGANIZATOR's bearer, because
 * `/marketing/campaigns` matches nothing in its `exeptAuthRequestUrl`. A marketolog page would
 * have issued an organizer-authenticated request whenever the session was missing — e.g. a
 * logout in a second tab (there is no `storage` listener) or `marketologGuard.deny()` clearing
 * the key while a request is already in flight. Unauthenticated is the correct outcome: the
 * backend answers 401 and the guard bounces the user to `/marketolog-login`.
 *
 * The guards are evaluated BEFORE the storage read (the owner interceptor reads first): this
 * interceptor is global, so a malformed `marketologTokenData` must not be able to throw on every
 * request the app makes. `getSession()` is deliberately not reused here — injecting the session
 * service, which itself depends on `HttpClient`, into an interceptor is a DI cycle.
 *
 * URL guard scope: every `/event-marketolog-management/**` and every `/marketing/**` request
 * issued from the marketolog shell. `/marketing/**` is matched wholesale — the endpoints outside
 * the marketolog's grant answer 403 for this role by design, they are not filtered here.
 */
export const marketologAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const isMarketologPath =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/marketolog');
  if (!isMarketologPath) return next(req);

  const isMarketologUrl =
    req.url.includes('/event-marketolog-management/') || req.url.includes('/marketing/');
  if (!isMarketologUrl) return next(req);

  const raw = localStorage.getItem(localStorageKeys.marketologTokenData);
  let token: string | undefined;
  if (raw) {
    try {
      token = (JSON.parse(raw) as { token?: string })?.token;
    } catch {
      token = undefined;
    }
  }

  // No session → sentinel only: claim the request so no other interceptor can authenticate it.
  const setHeaders: Record<string, string> = token
    ? { Authorization: `Bearer ${token}`, [SCOPED_AUTH_SENTINEL_HEADER]: '1' }
    : { [SCOPED_AUTH_SENTINEL_HEADER]: '1' };

  return next(req.clone({ setHeaders }));
};
